import { ENV } from '@forwardflow/helpers/configs'
import applyPaginationQueryParams from '@forwardflow/helpers/utils/applyPaginationQueryParams'
import {
   apiDelete,
   apiGet,
   apiPatch,
   apiPost,
} from '@forwardflow/services/Axios/configs'
import {
   CampaignData,
   IActivateCampaign,
   IActiveLeadSourceDetails,
   IAllCampaign,
   IAllLeadSources,
   IAssistantsVoices,
   ICampaignActivationSummary,
   ICampaignAnalytics,
   ICampaignOverview,
   ICampaignsData,
   ICampaignsOverview,
   IOrganizationCampaignDetailsById,
   IPrompt,
   PaginationQueryParams,
   ResponseSuccess,
} from '@forwardflow/types'
import {
   ICampaignQuestions,
   ICampaignUSP,
} from '@forwardflow/types/organization'

export default class CampaignService {
   // Extract campaignFilters logic into a private static method
   private static buildCampaignFilters(eligible_criteria?: CampaignData) {
      return {
         eligible_criteria: [
            eligible_criteria?.filters?.find((f) => f?.type === 'source')
               ?.sources?.length
               ? {
                    field: 'source',
                    operator: 'CONTAINS_ANY',
                    value: eligible_criteria.filters.find(
                       (f) => f?.type === 'source'
                    )?.sources,
                    logic: 'AND',
                 }
               : null,
            eligible_criteria?.filters?.find((f) => f?.type === 'stages')
               ?.stages?.length
               ? {
                    field: 'stage',
                    operator: 'CONTAINS_ANY',
                    value: eligible_criteria.filters.find(
                       (f) => f?.type === 'stages'
                    )?.stages,
                    logic: 'AND',
                 }
               : null,
            eligible_criteria?.filters?.find((f) => f?.type === 'tags')?.tags
               ?.length
               ? {
                    field: 'tags',
                    operator:
                       eligible_criteria.filters.find((f) => f?.type === 'tags')
                          ?.tagFilterType ?? 'CONTAINS_ALL',
                    value: eligible_criteria.filters.find(
                       (f) => f?.type === 'tags'
                    )?.tags,
                    logic: 'AND',
                 }
               : null,
            eligible_criteria?.filters?.find((f) => f?.type === 'created')
               ?.startDate ||
            eligible_criteria?.filters?.find((f) => f?.type === 'created')
               ?.endDate
               ? {
                    field: 'created_at',
                    operator: (() => {
                       const createdFilter = eligible_criteria.filters.find(
                          (f) => f?.type === 'created'
                       )
                       if (createdFilter?.dateFilterType === 'before')
                          return '<='
                       if (createdFilter?.dateFilterType === 'after')
                          return '>='
                       return 'BETWEEN'
                    })(),
                    value: (() => {
                       const createdFilter = eligible_criteria.filters.find(
                          (f) => f?.type === 'created'
                       )
                       if (createdFilter?.dateFilterType === 'before') {
                          return createdFilter?.startDate || ''
                       }
                       if (createdFilter?.dateFilterType === 'after') {
                          return createdFilter?.startDate || ''
                       }
                       // 'BETWEEN'
                       return [
                          createdFilter?.startDate || '',
                          createdFilter?.endDate || '',
                       ]
                    })(),
                    logic: 'AND',
                 }
               : null,
            eligible_criteria?.filters?.find((f) => f?.type === 'age')?.ageDays
               ? {
                    field: 'age',
                    operator:
                       eligible_criteria.filters.find((f) => f?.type === 'age')
                          ?.ageFilterType ?? '<=',
                    value:
                       Number(
                          eligible_criteria.filters.find(
                             (f) => f?.type === 'age'
                          )?.ageDays
                       ) || 0,
                    logic: 'AND',
                 }
               : null,
            eligible_criteria?.filters?.find((f) => f?.type === 'Ponds')
               ?.includeOnlyUnassignedPondLeads
               ? {
                    field: 'pond_lead',
                    operator: '=',
                    value: true,
                    logic: 'AND',
                 }
               : null,
            eligible_criteria?.filters?.find((f) => f?.type === 'Ponds')
               ?.assignedPonds?.length
               ? {
                    field: 'assigned_pond_id',
                    operator: 'CONTAINS_ANY',
                    value: eligible_criteria.filters
                       .find((f) => f?.type === 'Ponds')
                       ?.assignedPonds?.map((pond) => Number(pond))
                       .filter((num) => !isNaN(num)),
                    logic: 'AND',
                 }
               : null,
         ].filter((criteria) => criteria !== null),
      }
   }

   // Update campaign business hour data
   static async updateBusinessHourData(leadSource: string, hourData: any) {
      const data = await apiPatch<ResponseSuccess<any>>({
         apiPath: `/active-lead-sources/${leadSource}/business-hours`,
         data: {
            days: hourData.days,
            allowAgentsToSetAvailability: hourData.allowAgentsToSetAvailability,
            timezone: hourData.timezone,
         },
      })
      return data.data
   }

   // Update campaign business hour data
   static async updateAppointmentTimeFramesData(
      leadSource: string,
      hourData: any
   ) {
      const data = await apiPatch<ResponseSuccess<any>>({
         apiPath: `/active-lead-sources/${leadSource}/appointment-timeframe`,
         data: {
            days: hourData.days,
            allowAgentsToSetAvailability: hourData.allowAgentsToSetAvailability,
            timezone: hourData.timezone,
         },
      })
      return data.data
   }

   // Update campaign usp
   static async updateCampaignUsp(leadSource: string, uspData: ICampaignUSP[]) {
      const data = await apiPatch<ResponseSuccess<ICampaignUSP[]>>({
         apiPath: `/active-lead-sources/${leadSource}/usps`,
         data: {
            usps: uspData,
         },
      })
      return data.data
   }

   // Update campaign questions
   static async updateCampaignQualifyingQuestions(
      leadSource: string,
      qualifyingQuestions: ICampaignQuestions[]
   ) {
      const response = await apiPatch<
         ResponseSuccess<IActiveLeadSourceDetails>
      >({
         apiPath: `/active-lead-sources/${leadSource}/qualifying-questions`,
         data: {
            qualifyingQuestions: qualifyingQuestions,
         },
      })
      return response?.data?.data
   }

   // Get campaigns data

   static async getCampaignsData() {
      const response = await apiGet<ResponseSuccess<ICampaignsData>>({
         apiPath: `/campaigns`,
      })
      return response.data
   }

   // Get all recommended campaigns
   static async getAllRecommendedCampaigns() {
      const response = await apiGet<ResponseSuccess<IAllCampaign[]>>({
         apiPath: `/organization-account/recommended-campaign`,
      })
      return response.data
   }

   // Get all lead sources

   static async getAllLeadSources(queryParams: PaginationQueryParams) {
      const response = await apiGet<ResponseSuccess<IAllLeadSources[]>>({
         apiPath: `/lead-sources?${applyPaginationQueryParams(queryParams)}`,
      })
      return response.data
   }

   // Get Active Campaign list
   static async getActiveCampaigns(queryParams: PaginationQueryParams) {
      const response = await apiGet<ResponseSuccess<any[]>>({
         apiPath: `/active-lead-sources?${applyPaginationQueryParams(
            queryParams
         )}`,
      })
      return response.data
   }

   // Get campaign by id

   static async getCampaignByLeadSource(
      leadSource: string,
      statType: 'source' | 'campaign' | null
   ) {
      const response = await apiGet<ResponseSuccess<ICampaignAnalytics>>({
         apiPath: `/active-lead-sources/${leadSource}/stats?statType=${statType}`,
      })
      return response.data
   }

   // Create active lead source

   static async createActiveLeadSource(leadSource: string) {
      const data = await apiPost<ResponseSuccess<object>>({
         apiPath: `/active-lead-sources`,
         data: {
            campaignName: leadSource,
         },
      })
      return data.data
   }

   // Discard draft campaign
   static async discardDraftCampaign(leadSource: string) {
      const response = await apiPost<ResponseSuccess<object>>({
         apiPath: `/active-lead-sources/${leadSource}/discard-draft`,
      })
      return response.data
   }

   // Apply draft campaign
   static async applyDraftCampaign(leadSource: string) {
      const response = await apiPost<ResponseSuccess<any>>({
         apiPath: `/active-lead-sources/${leadSource}/apply-draft`,
      })
      return response.data
   }

   // Get estimated leads - updated to use the reusable function
   static async getEstimatedLeads(eligible_criteria?: CampaignData) {
      const campaignFilters = this.buildCampaignFilters(eligible_criteria)
      const response = await apiPost<ResponseSuccess<{ count: number }>>({
         apiPath: `/active-lead-sources/estimated-leads`,
         data: campaignFilters.eligible_criteria,
      })
      return response.data
   }

   static async getEstimatedLeadsForCampaign(eligible_criteria?: CampaignData) {
      const response = await apiPost<ResponseSuccess<{ count: number }>>({
         apiPath: `/active-lead-sources/estimated-leads`,
         data: eligible_criteria,
      })
      return response.data
   }

   // Create custom active lead source - updated to use the reusable function
   static async createCustomActiveLeadSource(
      leadSource: string,
      campaignFiltersData?: any,
      campaignType?: string[] | string,
      firstToContact?: {
         bufferTime: number
      },
      bypassFilterTransformation?: boolean,
      activityCampaignFilterCriteria?: {
         events: string[]
         lookback_days: number | null
         triggers?: { type: 'pond'; value: number }[]
      } | null,
      leadTypeFilter?: string[],
   ) {
      let campaignFilters = this.buildCampaignFilters(campaignFiltersData)
      if (bypassFilterTransformation) {
         campaignFilters = campaignFiltersData as any
      }

      const data = await apiPost<ResponseSuccess<object>>({
         apiPath: `/active-lead-sources`,
         data: {
            campaignName: leadSource,
            campaignFilters,
            campaignType: campaignType || ['new-lead'],
            firstToContact: firstToContact,
            activityCampaignFilterCriteria: activityCampaignFilterCriteria || null,
            leadTypeFilter: leadTypeFilter || ['buyer', 'seller', 'rental'],
         },
      })
      return data.data
   }

   // Edit custom active lead source
   static async editCustomActiveLeadSource(
      leadSource: string,
      eligible_criteria?: CampaignData
   ) {
      const filters = this.buildCampaignFilters(eligible_criteria)

      const data = await apiPatch<ResponseSuccess<object>>({
         apiPath: `/active-lead-sources/` + leadSource + `/campaign-filters`,
         data: {
            eligibleCriteria: filters.eligible_criteria,
         },
      })
      return data.data
   }

   // Update campaign name
   static async updateCampaignName(leadSource: string, campaignName: string) {
      const data = await apiPatch<ResponseSuccess<object>>({
         apiPath: `/active-lead-sources/${leadSource}/campaign-data`,
         data: { campaignName },
      })
      return data.data
   }

   static async updateLeadTypeFilter(leadSource: string, leadTypeFilter: string[]) {
      const data = await apiPatch<ResponseSuccess<object>>({
         apiPath: `/active-lead-sources/${leadSource}/lead-type-filter`,
         data: { leadTypeFilter },
      })
      return data.data
   }

   static async updateCampaignType(leadSource: string, campaignType: string | string[]) {
      const data = await apiPatch<ResponseSuccess<object>>({
         apiPath: `/active-lead-sources/${leadSource}/campaign-type`,
         data: { campaignType },
      })
      return data.data
   }

   static async updateActivityFilterCriteria(
      leadSource: string,
      criteria: {
         events?: string[]
         lookback_days?: number | null
         triggers?: { type: string; value: any }[]
      }
   ) {
      const data = await apiPatch<ResponseSuccess<object>>({
         apiPath: `/active-lead-sources/${leadSource}/activity-filter-criteria`,
         data: criteria,
      })
      return data.data
   }

   static async updateCampaignStepTypes(
      leadSource: string,
      campaignStepTypes: string[]
   ) {
      const data = await apiPatch<ResponseSuccess<object>>({
         apiPath: `/active-lead-sources/${leadSource}/campaign-step-types`,
         data: { campaignStepTypes },
      })
      return data.data
   }

   // Available campaign-step "types" for the org (proxied by ff-be from the campaign-configs-worker).
   static async getCampaignStepTypeOptions(): Promise<string[]> {
      const data = await apiGet<ResponseSuccess<string[]>>({
         apiPath: `/active-lead-sources/campaign-step-type-options`,
      })
      return data?.data ?? []
   }

   // Get active lead sources data

   static async getActiveLeadSources(leadSource: string) {
      const response = await apiGet<ResponseSuccess<IActiveLeadSourceDetails>>({
         apiPath: `/active-lead-sources/${leadSource}`,
      })
      return response.data
   }

   // Get active lead sources data from draft

   static async getActiveLeadSourcesFromDraft(leadSource: string) {
      const response = await apiGet<ResponseSuccess<IActiveLeadSourceDetails>>({
         apiPath: `/active-lead-sources/${leadSource}/draft`,
      })
      return response.data
   }

   // Get campaigns overview

   static async getCampaignsOverview({
      type,
   }: {
      type: string
      time_frame?: string
      time_frame_unit?: string
   }) {
      const data = await apiGet<ResponseSuccess<ICampaignsOverview>>({
         apiPath: `/analytics/lead-source/overview?type=${type}`,
      })
      return data.data
   }

   // get organization campaign overview
   static async getCampaignOverview(leadSource: string) {
      const data = await apiGet<ResponseSuccess<ICampaignOverview>>({
         apiPath: `/active-lead-sources/${leadSource}/overview`,
      })
      return data.data
   }

   // get organization campaign details

   static async getOrganizationCampaignDetails(campaignId: string) {
      const data = await apiGet<ResponseSuccess<any>>({
         apiPath: `/organization-campaigns/${campaignId}`,
      })
      return data.data
   }

   // update campaign config settings
   static async updateCampaignConfigSettings(
      leadSource: string,
      configSettings: {
         numberOfPhoneNumbers?: number
         numberOfAssistants?: number
         isAdvanceConfiguration?: boolean
      }
   ) {
      const data = await apiPatch<ResponseSuccess<object>>({
         apiPath: `/active-lead-sources/${leadSource}/auto-assign-config`,
         data: {
            numberOfPhoneNumbers: configSettings.numberOfPhoneNumbers,
            numberOfAssistants: configSettings.numberOfAssistants,
            isAdvanceConfiguration: configSettings.isAdvanceConfiguration,
         },
      })
      return data.data
   }

   static async updateAgentAssignmentConfig(
      leadSource: string,
      config: {
         enabled?: boolean
         strategy?: 'round_robin' | 'jump_ball'
         agentIds?: number[]
      }
   ) {
      const data = await apiPatch<ResponseSuccess<object>>({
         apiPath: `/active-lead-sources/${leadSource}/agent-assignment-config`,
         data: config,
      })
      return data.data
   }

   // remove campaign phone number
   static async removeCampaignPhoneNumber(
      leadSource: string,
      phoneNumber: string
   ) {
      const data = await apiPost({
         apiPath: `/active-lead-sources/${leadSource}/remove-phone-numbers/`,
         data: {
            phoneNumbers: [phoneNumber],
         },
      })
      return data
   }

   // Get organization campaign by id

   static async getOrganizationCampaignById(campaignId: string) {
      const response = await apiGet<
         ResponseSuccess<IOrganizationCampaignDetailsById>
      >({
         apiPath: `/organization-campaigns/${campaignId}/stats`,
      })
      return response.data
   }

   // add campaign phone number
   static async addCampaignPhoneNumber(
      leadSource: string,
      phoneNumberData: {
         id: string

         phoneNumber: string
         friendlyName: string
         region: string
         locality: string
         meta: any
         isoCountry: string
         price: number
         status: string
         address: string
         postalCode: string
         longitude: string
         latitude: string
         rateCenter: string
         lata: string
         beta: boolean
         addressRequirements: string
         capabilities: {
            voice: boolean
            SMS: boolean
            MMS: boolean
         }
      }
   ) {
      const data = await apiPost<ResponseSuccess<object>>({
         apiPath: `/active-lead-sources/${leadSource}/reserve-phone-number/`,
         data: {
            friendlyName: phoneNumberData?.friendlyName,
            phoneNumber: phoneNumberData?.phoneNumber,
            lata: phoneNumberData?.lata,
            locality: phoneNumberData?.locality,
            rateCenter: phoneNumberData?.rateCenter,
            latitude: phoneNumberData?.latitude,
            longitude: phoneNumberData?.longitude,
            region: phoneNumberData?.region,
            postalCode: phoneNumberData?.postalCode || '',
            isoCountry: phoneNumberData?.isoCountry || 'US',
            addressRequirements: phoneNumberData?.addressRequirements,
            beta: phoneNumberData?.beta,
            capabilities: {
               voice: phoneNumberData?.capabilities?.voice,
               SMS: phoneNumberData?.capabilities?.SMS,
               MMS: phoneNumberData?.capabilities?.MMS,
            },
         },
      })
      return data.data
   }

   // remove campaign assistant
   static async removeCampaignAssistant(
      assistantId: number,
      leadSource: string
   ) {
      const data = await apiDelete({
         apiPath: `/active-lead-sources/${leadSource}/ai-assistant/${assistantId}`,
      })
      return data
   }

   // get campaign assistants voices

   static async getCampaignAssistantsVoice(_queryParams: PaginationQueryParams) {
      // Public active voice catalog from campaign-config-worker (via BFF proxy).
      // Rows are snake_case and already carry the fields the assistant UI/payload
      // need (voice_id, name, description, avatar, gender, age, accent, preview_url).
      const data = await apiGet<ResponseSuccess<IAssistantsVoices[]>>({
         apiPath: `/svc/campaigns/voices/public`,
      })
      return data.data
   }

   static async getVoices() {
      // Onboarding-visible public voices from campaign-config-worker (via BFF proxy).
      const data = await apiGet<
         ResponseSuccess<
            Array<{
               voice_id: string
               name: string
               description: string | null
               avatar: string | null
               gender: string | null
               preview_url: string | null
               updated_at: string
            }>
         >
      >({
         apiPath: `/svc/campaigns/voices/public?showOnOnboarding=true`,
      })
      return (data.data ?? []).map((voice) => ({
         voiceId: voice.voice_id,
         lastUpdated: voice.updated_at ?? '',
         name: voice.name,
         description: voice.description ?? '',
         avatar: voice.avatar ?? '',
         gender: voice.gender ?? '',
         previewUrl: voice.preview_url ?? '',
      }))
   }

   static async getExternalElevenLabsVoicePreview(
      voiceId: string
   ): Promise<{ success: boolean; data: ArrayBuffer }> {
      const response = await apiGet<any>({
         apiPath: `/voices/${voiceId}/preview`,
         config: {
            responseType: 'arraybuffer',
         },
         includeXOrg: false,
      })

      return response
   }
   // get preview voices

   static async getPreviewVoices(voiceId: string) {
      const data = await apiGet<ResponseSuccess<ArrayBuffer>>({
         apiPath: `/campaign-assistant/voices/${voiceId}/preview`,
         config: {
            responseType: 'arraybuffer',
         },
      })
      return data
   }

   static async getPrompts() {
      const data = await apiGet<ResponseSuccess<IPrompt[]>>({
         apiPath: `${ENV.CAMPAIGN_WORKER_URL}/all-prompts`,
         includeXOrg: false,
      })
      return data.data
   }

   // update campaign assistant

   static async updateOrganizationCampaignAssistants(
      leadSource: string,
      assistantId: number,
      assistantsData: {
         assistantName: string
         voiceId?: string
         description?: string
         gender?: string
         age?: string
         language?: string
         accent?: string
         designation?: string
         avatar?: string
         previewUrl?: string
      }
   ) {
      const data = await apiPatch<ResponseSuccess<object>>({
         apiPath: `active-lead-sources/${leadSource}/ai-assistant/${assistantId}`,
         data: {
            assistantName: assistantsData.assistantName,
            voiceId: assistantsData.voiceId,
            description: assistantsData.description,
            gender: assistantsData.gender,
            age: assistantsData.age,
            language: assistantsData.language,
            accent: assistantsData.accent,
            designation: assistantsData.designation,
            avatar: assistantsData.avatar,
            previewUrl: assistantsData.previewUrl,
         },
      })
      return data.data
   }

   // create campaign assistant

   static async createOrganizationCampaignAssistants(
      leadSource: string,
      assistantsData: {
         assistantName: string
         voiceId: string
         description: string
         gender?: string
         age?: string
         language?: string
         accent?: string
         designation?: string
         avatar: string
         previewUrl?: string
      }
   ) {
      const data = await apiPost<ResponseSuccess<object>>({
         apiPath: `active-lead-sources/${leadSource}/ai-assistant`,
         data: {
            assistantName: assistantsData.assistantName,
            voiceId: assistantsData.voiceId,
            description: assistantsData.description,
            gender: assistantsData.gender,
            age: assistantsData.age,
            language: assistantsData.language,
            accent: assistantsData.accent,
            designation: assistantsData.designation,
            avatar: assistantsData.avatar,
            previewUrl: assistantsData.previewUrl,
         },
      })
      return data.data
   }

   // create campaign assistant in bulk

   static async createOrganizationCampaignAssistantsInBulk(
      leadSource: string,
      assistants: Array<{
         assistantName: string
         voiceId: string
         description: string
         designation?: string
         avatar: string
         gender?: string
         age?: string
         language?: string
         accent?: string
         previewUrl?: string
      }>
   ) {
      const data = await apiPost<ResponseSuccess<object>>({
         apiPath: `/active-lead-sources/${leadSource}/ai-assistant/bulk`,
         data: {
            assistants: assistants,
         },
      })
      return data.data
   }

   // get campaign activation summary

   static async getCampaignActivationSummary(leadSource: string) {
      const data = await apiGet<ResponseSuccess<ICampaignActivationSummary>>({
         apiPath: `/active-lead-sources/${leadSource}/activation-summary`,
      })
      return data.data
   }

   // activate campaign

   static async activateCampaign(leadSource: string) {
      const data = await apiPost<ResponseSuccess<IActivateCampaign>>({
         apiPath: `/active-lead-sources/${leadSource}/activate`,
      })
      return data.data
   }

   // update campaign status

   static async updateCampaignStatus(leadSource: string, status: string) {
      const data = await apiPatch<ResponseSuccess<object>>({
         apiPath: `/active-lead-sources/${leadSource}/status`,
         data: {
            status: status,
         },
      })
      return data.data
   }

   // update first to contact

   static async updateFirstToContact(
      lead: string,
      firstToContact: {
         tags: string[]
         type: string
         stages: string[]
         bufferTime: number | null
      }
   ) {
      const data = await apiPatch<ResponseSuccess<object>>({
         apiPath: `/active-lead-sources/${lead}/first-to-contact`,
         data: {
            tags: firstToContact.tags,
            type: firstToContact.type,
            stages: firstToContact.stages,
            bufferTime: firstToContact.bufferTime,
         },
      })
      return data.data
   }
}
